import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Store, MapPin, Phone, Mail, Globe, Clock, Plus, X } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';
import { type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { ShopOpeningHoursEditor } from '../components/ShopOpeningHoursEditor';
import { type ShopOpeningHoursForm } from '../utils/openingHours';

interface ShopFormData {
  name: string;
  companyId?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  phone: string;
  email: string;
  website: string;
  socialMedia: {
    instagram: string;
    facebook: string;
    tiktok: string;
  };
  openingHours: ShopOpeningHoursForm;
  services: Array<{
    serviceId: string;
    available: boolean;
    customPricing?: number;
  }>;
  facilities: Array<{
    type: string;
    description: string;
    available: boolean;
  }>;
  policies: {
    cancellation: string;
    deposit: string;
    aftercare: string;
    photography: string;
  };
  isActive: boolean;
}

export function ShopCreatePage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<ShopFormData>({
    name: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    phone: '',
    email: '',
    website: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      tiktok: ''
    },
    openingHours: {
      monday: [{ open: '10:00', close: '19:00' }],
      tuesday: [{ open: '10:00', close: '19:00' }],
      wednesday: [{ open: '10:00', close: '19:00' }],
      thursday: [{ open: '10:00', close: '19:00' }],
      friday: [{ open: '10:00', close: '20:00' }],
      saturday: [{ open: '10:00', close: '20:00' }]
    },
    services: [],
    facilities: [],
    policies: {
      cancellation: '24h de préavis requis pour les annulations',
      deposit: 'Acompte de 15€ requis pour les réservations piercing',
      aftercare: 'Instructions de soins post-intervention fournies',
      photography: 'Photos prises avec consentement du client'
    },
    isActive: true
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<Array<{id: string; name: string}>>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/companies?limit=100'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCompanies(data.data.companies.map((c: any) => ({
            id: c.id,
            name: c.name
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    const keys = field.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const addFacility = () => {
    setFormData(prev => ({
      ...prev,
      facilities: [
        ...prev.facilities,
        {
          type: '',
          description: '',
          available: true
        }
      ]
    }));
  };

  const removeFacility = (index: number) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.filter((_, i) => i !== index)
    }));
  };

  const updateFacility = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.map((facility, i) => 
        i === index ? { ...facility, [field]: value } : facility
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneDisplay.trim() || !phoneIsValid || !phoneE164) {
      setError('Le numéro de téléphone est invalide pour le pays sélectionné.');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/shops'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, phone: phoneE164 }),
      });

      const data = await response.json();
      
      if (data.success) {
        navigate('/shops');
      } else {
        setError(data.message || 'Échec de la création du shop');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Create shop error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/shops');
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Ajouter un Shop</span>
              <HelpIcon 
                docPath="04-boutiques#04-3-creation" 
                tooltip="Documentation : Création d'une boutique"
              />
            </h1>
            <p className="text-textSecondary">Créer un nouveau point de vente</p>
          </div>
        </div>

        <button
          type="submit"
          form="shop-form"
          disabled={loading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{loading ? 'Création...' : 'Créer le Shop'}</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form id="shop-form" onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Store className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom du Shop *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input-field"
                placeholder="Digital Tattoo Paris"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Compagnie
              </label>
              <select
                value={formData.companyId || ''}
                onChange={(e) => handleInputChange('companyId', e.target.value || undefined)}
                className="input-field"
                disabled={loadingCompanies}
              >
                <option value="">Aucune compagnie</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
              {loadingCompanies && (
                <p className="mt-1 text-sm text-gray-500">Chargement des compagnies...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Téléphone *
              </label>
              <InternationalPhoneInput
                value={phoneDisplay}
                country={phoneCountry}
                onChange={(result) => {
                  setPhoneDisplay(result.displayValue);
                  setPhoneCountry(result.country);
                  setPhoneE164(result.e164Value);
                  setPhoneIsValid(result.isValid);
                  handleInputChange('phone', result.displayValue);
                  if (result.isValid) {
                    setError((prev) =>
                      prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                    );
                  }
                }}
                selectClassName="input-field"
                inputClassName="input-field"
                placeholder="+33 1 23 45 67 89"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
                placeholder="info@digitaltattoo.fr"
              />
            </div>

            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                Site Web
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                className="input-field"
                placeholder="https://digitaltattoo.fr"
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
              <MapPin className="w-5 h-5 text-accent-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Adresse</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Adresse *
              </label>
              <input
                type="text"
                required
                value={formData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                className="input-field"
                placeholder="123 Rue de l'Art"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Ville *
              </label>
              <input
                type="text"
                required
                value={formData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                className="input-field"
                placeholder="Paris"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Code Postal *
              </label>
              <input
                type="text"
                required
                value={formData.address.postalCode}
                onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                className="input-field"
                placeholder="75001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Pays *
              </label>
              <select
                value={formData.address.country}
                onChange={(e) => handleInputChange('address.country', e.target.value)}
                className="input-field"
              >
                <option value="France">France</option>
                <option value="Belgium">Belgique</option>
                <option value="Switzerland">Suisse</option>
                <option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>

        {/* Opening Hours */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Horaires d'Ouverture</h2>
          </div>

          <ShopOpeningHoursEditor
            value={formData.openingHours}
            onChange={(next) => handleInputChange('openingHours', next)}
          />
        </div>

        {/* Social Media */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Réseaux Sociaux</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Instagram
              </label>
              <input
                type="text"
                value={formData.socialMedia.instagram}
                onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                className="input-field"
                placeholder="@digitaltattoo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Facebook
              </label>
              <input
                type="text"
                value={formData.socialMedia.facebook}
                onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                className="input-field"
                placeholder="digitaltattoo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                TikTok
              </label>
              <input
                type="text"
                value={formData.socialMedia.tiktok}
                onChange={(e) => handleInputChange('socialMedia.tiktok', e.target.value)}
                className="input-field"
                placeholder="@digitaltattoo"
              />
            </div>
          </div>
        </div>

        {/* Facilities */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                <Store className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-textPrimary">Équipements</h2>
            </div>
            <button
              type="button"
              onClick={addFacility}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Ajouter un équipement</span>
            </button>
          </div>

          {formData.facilities.map((facility, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-textPrimary">Équipement {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => removeFacility(index)}
                  className="text-red-600 hover:text-red-700 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Type
                  </label>
                  <input
                    type="text"
                    value={facility.type}
                    onChange={(e) => updateFacility(index, 'type', e.target.value)}
                    className="input-field"
                    placeholder="Salle privée"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={facility.description}
                    onChange={(e) => updateFacility(index, 'description', e.target.value)}
                    className="input-field"
                    placeholder="3 salles privées pour tatouage"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={facility.available}
                      onChange={(e) => updateFacility(index, 'available', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-textPrimary">Disponible</span>
                  </label>
                </div>
              </div>
            </div>
          ))}

          {formData.facilities.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p>Aucun équipement ajouté</p>
              <p className="text-sm">Cliquez sur "Ajouter un équipement" pour commencer</p>
            </div>
          )}
        </div>

        {/* Policies */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
              <Globe className="w-5 h-5 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Politiques du Shop</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Politique d'Annulation
              </label>
              <textarea
                value={formData.policies.cancellation}
                onChange={(e) => handleInputChange('policies.cancellation', e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Conditions d'annulation des rendez-vous"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Politique d'Acompte
              </label>
              <textarea
                value={formData.policies.deposit}
                onChange={(e) => handleInputChange('policies.deposit', e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Conditions de versement d'acompte"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Politique de Soins
              </label>
              <textarea
                value={formData.policies.aftercare}
                onChange={(e) => handleInputChange('policies.aftercare', e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Instructions et politiques de soins post-intervention"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Politique de Photographie
              </label>
              <textarea
                value={formData.policies.photography}
                onChange={(e) => handleInputChange('policies.photography', e.target.value)}
                rows={2}
                className="input-field"
                placeholder="Conditions de prise de photos et d'utilisation"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <h2 className="text-xl font-semibold text-textPrimary">Statut</h2>
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-textPrimary">Shop actif</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              Les shops inactifs n'apparaîtront pas dans les options de réservation
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
